import {
    DashboardCustomRole,
    DashboardFeature,
    DashboardFeatureAccess,
    DashboardFeatureAccessByRole,
    DashboardRole,
    FixedDashboardRole,
    HealthcareProvider,
    Therapist,
    UserType,
} from './interfaces';

export const FIXED_DASHBOARD_ROLES: Array<{ id: FixedDashboardRole; label: string }> = [
    { id: 'root', label: 'Root' },
    { id: 'super', label: 'Super' },
    { id: 'admin', label: 'Admin' },
    { id: 'healthcare_professional', label: 'Healthcare Professional' },
];

export const DASHBOARD_FEATURES: Array<{ id: DashboardFeature; label: string }> = [
    { id: 'clients', label: 'Clients page' },
    { id: 'client_plans', label: 'Client plans' },
    { id: 'audio_scribe_reports', label: 'Audio scribe and reports' },
    { id: 'messaging', label: 'Messaging page' },
    { id: 'development', label: 'Development areas' },
    { id: 'provider_selector', label: 'Select healthcare provider' },
    { id: 'organisation_analytics_heatmap', label: 'Organisation analytics and heatmap' },
    { id: 'organisation_team', label: 'View organisation team' },
    { id: 'organisation_team_manage', label: 'Manage organisation team' },
];

export const PLATFORM_ONLY_DASHBOARD_FEATURES: Array<DashboardFeature> = [
    'development',
    'provider_selector',
];

export const CONFIGURABLE_DASHBOARD_FEATURES = DASHBOARD_FEATURES.filter(
    (feature) => !PLATFORM_ONLY_DASHBOARD_FEATURES.includes(feature.id)
);

// Existing providers keep the original features; Client plans requires explicit provider opt-in.
export function isDashboardFeatureAvailableForProvider(
    provider: Pick<HealthcareProvider, 'dashboardFeatureAvailability'> | undefined | null,
    feature: DashboardFeature
): boolean {
    if (PLATFORM_ONLY_DASHBOARD_FEATURES.includes(feature)) {
        return true;
    }
    const availability = provider?.dashboardFeatureAvailability;
    if (feature === 'client_plans') {
        return availability?.client_plans === true && availability.clients !== false;
    }
    return availability?.[feature] !== false &&
        (feature !== 'organisation_team_manage' || availability?.organisation_team !== false);
}

// Self-service plans cap the existing role matrix; manual providers stay unrestricted.
export function isSelfServiceProvider(
    provider: Pick<HealthcareProvider, 'commercialModel'> | undefined | null
): boolean {
    return provider?.commercialModel === 'self_service';
}

export function isDashboardFeatureIncludedInPlan(
    provider: Pick<HealthcareProvider, 'commercialModel' | 'allowedDashboardFeatures' | 'entitlement'> | undefined | null,
    feature: DashboardFeature
): boolean {
    if (!isSelfServiceProvider(provider)) {
        return true;
    }

    // The provider document is the live feature projection; billing entitlement is a legacy fallback.
    const providerProjection = provider?.allowedDashboardFeatures;
    const allowedFeatures = Array.isArray(providerProjection) ?
        providerProjection : provider?.entitlement?.allowedDashboardFeatures;
    // Client plans follows the Clients entitlement without requiring old plan catalogues to be migrated.
    const planFeature = feature === 'client_plans' ? 'clients' : feature;
    return Array.isArray(allowedFeatures) && allowedFeatures.includes(planFeature);
}

/** Hold the dashboard until its selected provider and SaaS feature projection are available. */
export function isDashboardProviderHydrated(
    authenticatedUser: { uid: string } | null | undefined,
    therapist: Pick<Therapist, 'id' | 'healthcareProviderId'> | null | undefined,
    selectedProviderId: string,
    provider: Pick<HealthcareProvider, 'id' | 'commercialModel' | 'allowedDashboardFeatures'> | null | undefined
): boolean {
    if (!authenticatedUser?.uid || !therapist?.id || !selectedProviderId ||
        therapist.healthcareProviderId === '' || provider?.id !== selectedProviderId) {
        return false;
    }
    return provider.commercialModel !== 'self_service' || Array.isArray(provider.allowedDashboardFeatures);
}

const ALL_FEATURES_ENABLED: DashboardFeatureAccess = DASHBOARD_FEATURES.reduce<DashboardFeatureAccess>(
    (access, feature) => ({ ...access, [feature.id]: true }),
    {}
);

// Defaults give each fixed role a useful baseline until a provider saves its own matrix.
export const DEFAULT_DASHBOARD_FEATURE_ACCESS: Record<FixedDashboardRole, DashboardFeatureAccess> = {
    root: ALL_FEATURES_ENABLED,
    super: ALL_FEATURES_ENABLED,
    admin: {
        clients: true,
        client_plans: true,
        audio_scribe_reports: true,
        messaging: false,
        development: false,
        provider_selector: false,
        organisation_analytics_heatmap: true,
        organisation_team: true,
        organisation_team_manage: true,
    },
    healthcare_professional: {
        clients: true,
        client_plans: true,
        audio_scribe_reports: true,
        messaging: false,
        development: false,
        provider_selector: false,
        organisation_analytics_heatmap: false,
        organisation_team: false,
        organisation_team_manage: false,
    },
};

// Prefer the new role field while retaining legacy super users and therapist records.
export function getDashboardRole(therapist?: Partial<Therapist> | null): DashboardRole {
    const storedRole = typeof therapist?.role === 'string' ? therapist.role.trim() : '';
    if (storedRole) {
        return storedRole;
    }

    return therapist?.super ? 'super' : 'healthcare_professional';
}

export function isFixedDashboardRole(role: DashboardRole): role is FixedDashboardRole {
    return FIXED_DASHBOARD_ROLES.some((candidateRole) => candidateRole.id === role);
}

export function isRootOrSuperRole(role: DashboardRole): boolean {
    return role === 'root' || role === 'super';
}

export function getUserTypeForDashboardRole(role: DashboardRole): UserType {
    if (role === 'root' || role === 'super' || role === 'admin') {
        return role;
    }

    return 'therapist';
}

// Read an explicit provider value first, then fall back to fixed-role defaults.
export function hasDashboardFeature(
    provider: Pick<HealthcareProvider,
        'dashboardFeatureAccess' | 'dashboardFeatureAvailability' | 'commercialModel' | 'allowedDashboardFeatures' | 'entitlement'
    > | undefined | null,
    role: DashboardRole,
    feature: DashboardFeature
): boolean {
    // Provider switches apply to every role; platform tools remain available to restore access.
    if ((!provider && !PLATFORM_ONLY_DASHBOARD_FEATURES.includes(feature)) ||
        !isDashboardFeatureAvailableForProvider(provider, feature)) {
        return false;
    }
    // Plans share the Client boundary even when their own role permission was saved as enabled.
    if (feature === 'client_plans' && !hasDashboardFeature(provider, role, 'clients')) {
        return false;
    }
    // Platform roles bypass role and plan limits only for features the provider still offers.
    if (isRootOrSuperRole(role)) {
        return true;
    }
    if (!provider) {
        return false;
    }
    if (PLATFORM_ONLY_DASHBOARD_FEATURES.includes(feature)) {
        return false;
    }

    if (!isDashboardFeatureIncludedInPlan(provider, feature)) {
        return false;
    }

    // The two SaaS role invariants prevent a professional from escalating team access.
    if (isSelfServiceProvider(provider) && feature === 'organisation_team_manage') {
        return role === 'admin';
    }

    const roleAccess = provider.dashboardFeatureAccess?.[role];
    // Management always includes the ability to view the Team tab.
    if (feature === 'organisation_team' && roleAccess?.organisation_team_manage === true) {
        return true;
    }

    const configuredValue = roleAccess?.[feature];
    if (typeof configuredValue === 'boolean') {
        return configuredValue;
    }

    // Older and custom role rows inherit Clients permission until a separate plans choice is saved.
    if (feature === 'client_plans') {
        return hasDashboardFeature(provider, role, 'clients');
    }

    // Before the split, Admin's single Team flag controlled both viewing and management.
    if (feature === 'organisation_team_manage' && role === 'admin' &&
        typeof roleAccess?.organisation_team === 'boolean') {
        return roleAccess.organisation_team;
    }

    return isFixedDashboardRole(role) ? !!DEFAULT_DASHBOARD_FEATURE_ACCESS[role][feature] : false;
}

// Materialise every checkbox so saving a matrix never depends on implicit defaults.
export function getCompleteDashboardFeatureAccess(
    accessByRole: DashboardFeatureAccessByRole | undefined,
    roles: Array<DashboardRole>
): DashboardFeatureAccessByRole {
    const provider = { dashboardFeatureAccess: accessByRole };
    return roles.reduce<DashboardFeatureAccessByRole>((completeAccess, role) => {
        completeAccess[role] = DASHBOARD_FEATURES.reduce<DashboardFeatureAccess>((roleAccess, feature) => {
            // Availability never rewrites saved plan permissions, including while the column is hidden.
            if (feature.id === 'client_plans') {
                const configured = accessByRole?.[role]?.client_plans;
                roleAccess.client_plans = typeof configured === 'boolean' ? configured :
                    hasDashboardFeature(provider, role, 'clients');
                return roleAccess;
            }
            roleAccess[feature.id] = hasDashboardFeature(provider, role, feature.id);
            return roleAccess;
        }, {});
        return completeAccess;
    }, {});
}

// Clamp a complete SaaS matrix before it is sent to the server-owned plan validator.
export function clampDashboardFeatureAccessToPlan(
    accessByRole: DashboardFeatureAccessByRole,
    allowedFeatures: Array<DashboardFeature>
): DashboardFeatureAccessByRole {
    const allowed = new Set(allowedFeatures);
    // The plan's Clients entitlement also includes the independently controlled Client plans feature.
    if (allowed.has('clients')) allowed.add('client_plans');
    const access = Object.fromEntries(Object.entries(accessByRole).map(([role, row]) => [
        role,
        { ...row },
    ])) as DashboardFeatureAccessByRole;
    Object.values(access).forEach((row) => {
        CONFIGURABLE_DASHBOARD_FEATURES.forEach((feature) => {
            if (!allowed.has(feature.id)) {
                row[feature.id] = false;
            }
        });
    });
    access.admin ||= {};
    access.healthcare_professional ||= {};
    access.admin.organisation_team = true;
    access.admin.organisation_team_manage = true;
    access.healthcare_professional.organisation_team_manage = false;
    return access;
}

export function getDashboardRoleLabel(role: DashboardRole, customRoles: Array<DashboardCustomRole> = []): string {
    return FIXED_DASHBOARD_ROLES.find((candidateRole) => candidateRole.id === role)?.label ||
        customRoles.find((customRole) => customRole.id === role)?.name ||
        role;
}

export function getAllDashboardRoles(customRoles: Array<DashboardCustomRole> = []): Array<{ id: DashboardRole; label: string }> {
    return [
        ...FIXED_DASHBOARD_ROLES,
        ...customRoles.map((customRole) => ({ id: customRole.id, label: customRole.name })),
    ];
}

export function getConfigurableDashboardRoles(
    customRoles: Array<DashboardCustomRole> = []
): Array<{ id: DashboardRole; label: string }> {
    return getAllDashboardRoles(customRoles).filter((role) => !isRootOrSuperRole(role.id));
}

export function canViewProviderTeam(
    provider: Pick<HealthcareProvider,
        'dashboardFeatureAccess' | 'dashboardFeatureAvailability' | 'commercialModel' | 'allowedDashboardFeatures' | 'entitlement'
    > | undefined | null,
    role: DashboardRole
): boolean {
    return hasDashboardFeature(provider, role, 'organisation_team');
}

export function canManageProviderTeam(
    provider: Pick<HealthcareProvider,
        'dashboardFeatureAccess' | 'dashboardFeatureAvailability' | 'commercialModel' | 'allowedDashboardFeatures' | 'entitlement'
    > | undefined | null,
    role: DashboardRole
): boolean {
    return hasDashboardFeature(provider, role, 'organisation_team_manage');
}

// Platform staff retain legacy access; SaaS organisations may configure only through their Admin role.
export function canConfigureProviderDashboardAccess(
    provider: Pick<HealthcareProvider, 'commercialModel'> | undefined | null,
    role: DashboardRole
): boolean {
    return isRootOrSuperRole(role) || (role === 'admin' && isSelfServiceProvider(provider));
}

// Team-management entitlement is checked separately; this protects the platform-role boundary.
export function canAssignDashboardRole(actorRole: DashboardRole, targetRole: DashboardRole): boolean {
    if (actorRole === 'root') {
        return true;
    }
    if (actorRole === 'super') {
        return targetRole !== 'root';
    }
    if (actorRole && actorRole !== 'client' && actorRole !== 'unauthenticated') {
        return targetRole !== 'root' && targetRole !== 'super';
    }
    return false;
}

export function createCustomDashboardRoleId(name: string): string {
    const slug = name
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '');
    return slug ? `custom_${slug}` : '';
}
